import { BUILTINS, IMPLIED_GROUPS, POD_BUILTINS } from './completion';
import { CliCommandSpec } from 'types/cli';

// Resolves words the way lineParser does, so only what the server would accept is coloured as a keyword.
export type TokenRole =
  | 'group'
  | 'command'
  | 'builtin'
  | 'option'
  | 'string'
  | 'arg'
  | 'unknown'
  | 'space';

export interface CommandToken {
  text: string;
  role: TokenRole;
}

/** A word as typed (quotes included, so it renders back unchanged) and unquoted (to match on). */
interface Word {
  raw: string;
  value: string;
}

type Part = { word: Word } | { space: string };

/** Splits a line into words and the whitespace between them, keeping every character. */
const scan = (line: string): Part[] => {
  const parts: Part[] = [];
  let raw = '';
  let value = '';
  let space = '';
  let quote: string | null = null;

  const endWord = () => {
    if (raw) parts.push({ word: { raw, value } });
    raw = '';
    value = '';
  };
  const endSpace = () => {
    if (space) parts.push({ space });
    space = '';
  };

  for (const char of line) {
    if (quote) {
      raw += char;
      if (char === quote) quote = null;
      else value += char;
    } else if (char === '"' || char === "'") {
      endSpace();
      quote = char;
      raw += char;
    } else if (/\s/.test(char)) {
      endWord();
      space += char;
    } else {
      endSpace();
      raw += char;
      value += char;
    }
  }

  endWord();
  endSpace();
  return parts;
};

/** The roles of the first one or two words: the keywords. Everything after them is an argument. */
const keywordRoles = (
  words: Word[],
  commands: CliCommandSpec[],
  podTab: boolean,
  tabGroup?: string,
): TokenRole[] => {
  const first = words[0].value;
  const has = (category: string, command: string) =>
    commands.some(
      spec => spec.category === category && spec.command === command,
    );

  if (commands.some(spec => spec.category === first)) {
    const second = words[1]?.value;
    if (second === undefined) return ['group'];
    return ['group', has(first, second) ? 'command' : 'unknown'];
  }

  if (BUILTINS.includes(first) || (podTab && POD_BUILTINS.includes(first)))
    return ['builtin'];

  // Same precedence as lineParser's implyGroup: the tab's own group, then logs, then exec.
  if (podTab) {
    if (tabGroup && has(tabGroup, first)) return ['command'];
    return [
      has(IMPLIED_GROUPS[first] ?? 'exec', first) ? 'command' : 'unknown',
    ];
  }

  return ['unknown'];
};

export function highlightCommand(
  line: string,
  commands: CliCommandSpec[],
  podTab = true,
  tabGroup?: string,
): CommandToken[] {
  const parts = scan(line);
  const words = parts.flatMap(part => ('word' in part ? [part.word] : []));
  const keywords = words.length
    ? keywordRoles(words, commands, podTab, tabGroup)
    : [];

  let index = -1;
  return parts.map(part => {
    if ('space' in part) return { text: part.space, role: 'space' };
    index += 1;
    const { raw, value } = part.word;
    // Past the keywords: options, quoted strings, and plain positionals.
    const role =
      keywords[index] ??
      (value.startsWith('-') && value !== '-'
        ? 'option'
        : raw === value
          ? 'arg'
          : 'string');
    return { text: raw, role };
  });
}
